import { provideImgixLoader } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { BASE_PATH, BASE_PATH_V2, ConfigurationV2 } from 'api-client';
import { providePrimeNG } from 'primeng/config';
import { LOG_CONFIG, LogConfig } from 'shared';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { authInterceptor } from './features/authentication/services/auth.interceptor';
import { apiKeyInterceptor } from './interceptors/api-key.interceptor';
import { impersonationInterceptor } from './interceptors/impersonation.interceptor';

const Noir = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{zinc.50}',
      100: '{zinc.100}',
      200: '{zinc.200}',
      300: '{zinc.300}',
      400: '{zinc.400}',
      500: '{zinc.500}',
      600: '{zinc.600}',
      700: '{zinc.700}',
      800: '{zinc.800}',
      900: '{zinc.900}',
      950: '{zinc.950}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{zinc.950}',
          inverseColor: '#ffffff',
          hoverColor: '{zinc.900}',
          activeColor: '{zinc.800}',
        },
        highlight: {
          background: '{zinc.950}',
          focusBackground: '{zinc.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        primary: {
          color: '{zinc.50}',
          inverseColor: '{zinc.950}',
          hoverColor: '{zinc.100}',
          activeColor: '{zinc.200}',
        },
        highlight: {
          background: 'rgba(250, 250, 250, .16)',
          focusBackground: 'rgba(250, 250, 250, .24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Noir,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([impersonationInterceptor, authInterceptor, apiKeyInterceptor]),
    ),
    importProvidersFrom(TranslateModule.forRoot()),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),
    provideImgixLoader('https://picsum.photos/'),
    {
      provide: BASE_PATH,
      useValue: environment.API_BASE_PATH,
    },
    {
      provide: BASE_PATH_V2,
      useValue: environment.API_BASE_PATH,
    },
    {
      provide: ConfigurationV2,
      useFactory: () =>
        new ConfigurationV2({
          basePath: environment.API_BASE_PATH,
          withCredentials: true,
        }),
    },
    {
      provide: LOG_CONFIG,
      useValue: {
        source: environment.logging?.source || 'admin-app',
        enableV2Logging: environment.logging?.enableV2Logging ?? false,
        bufferSize: environment.logging?.bufferSize ?? 25,
        flushIntervalMs: environment.logging?.flushIntervalMs ?? 5000,
      } as LogConfig,
    },
  ],
};
